import { useEffect, useState } from 'react';
import {
  Alert, Avatar, CircularProgress, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import api from '../api';
import type { PeriodType, Standing } from '../api/types';

interface Props {
  periodType: PeriodType;
  periodValue: number;
}

interface Result {
  key: string;
  standings: Standing[] | null;
  error: string | null;
}

export default function ResultsTable({ periodType, periodValue }: Props) {
  const requestKey = `${periodType}:${periodValue}`;
  // Keyed by the request it answers, so "loading" is derived rather than set from an effect.
  const [result, setResult] = useState<Result>({ key: '', standings: null, error: null });

  useEffect(() => {
    let live = true;
    api.standings(periodType, periodValue)
      .then((response) => {
        if (!live) {
          return;
        }
        if (response.statusCode === 200) {
          setResult({ key: requestKey, standings: response.docs ?? [], error: null });
        } else {
          setResult({
            key: requestKey,
            standings: null,
            error: response.messages.join(' ') || 'Nie udało się pobrać wyników'
          });
        }
      })
      .catch((e: Error) => {
        if (live) {
          setResult({ key: requestKey, standings: null, error: e.message });
        }
      });
    return () => { live = false; };
  }, [periodType, periodValue, requestKey]);

  if (result.key !== requestKey) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Wczytywanie wyników…</Typography>
      </Stack>
    );
  }

  if (result.error) {
    return <Alert severity="error" sx={{ my: 1 }}>{result.error}</Alert>;
  }

  const standings = result.standings ?? [];
  if (standings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Brak wyników w tym okresie.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 56 }} />
            <TableCell>Gracz</TableCell>
            <TableCell align="right">Punkty</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {standings.map((row, index) => (
            <TableRow key={row.player} hover>
              <TableCell>
                <Avatar sx={{ width: 32, height: 32, bgcolor: index === 0 ? 'primary.main' : 'grey.500' }}>
                  {row.player.slice(0, 1).toUpperCase()}
                </Avatar>
              </TableCell>
              <TableCell>{row.player}</TableCell>
              <TableCell align="right">{row.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
